import React from 'react'
import styled, { DefaultTheme, useTheme, css } from 'styled-components/native'

// Components
import Typography from '../Typography'
import Icon from '../Icon'
import Card from '../Card'
import DialogueMarkdown from 'APP/Components/DialogueMarkdown'

// Types
import { IconCardProps, IconContainerProps } from './types'

// Utils
import { useMarkdownStyles } from 'APP/Hooks/useMarkdownStyles'

const StyledIconCard = styled(Card)`
  display: flex;
  flex-direction: row;
  min-height: ${({ theme }) => theme.metrics.baseMargin * 4}px;
  shadow: ${({ theme }) => theme.shadows.soft};
  padding: 0px;
`

const StyledIconContainer = styled.View<{
  $background?: keyof DefaultTheme['colors']
  $position: 'left' | 'right'
}>`
  ${({ theme, $background, $position }) => css`
    align-items: center;
    justify-content: center;
    width: ${theme.metrics.icon.large + theme.metrics.baseMargin}px;
    background-color: ${$background ? theme.colors[$background] : 'transparent'};
    padding: ${theme.metrics.baseMargin}px;
    border-top-left-radius: ${$position === 'left' ? theme.metrics.borderRadius.small : 0}px;
    border-bottom-left-radius: ${$position === 'left' ? theme.metrics.borderRadius.small : 0}px;
    border-top-right-radius: ${$position === 'right' ? theme.metrics.borderRadius.small : 0}px;
    border-bottom-right-radius: ${$position === 'right' ? theme.metrics.borderRadius.small : 0}px;
  `}
`

const StyledContentContainer = styled.View`
  flex: 1;
  justify-content: center;
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const IconContainer = ({
  src,
  background,
  size,
  color = 'accent',
  variant = 'materialIcons',
  position,
}: IconContainerProps) => {
  const theme = useTheme()
  return (
    <StyledIconContainer
      $background={background}
      $position={position}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Icon src={src} size={theme.metrics.icon[size || 'small']} variant={variant} color={color} />
    </StyledIconContainer>
  )
}

const IconCard = ({
  title,
  description,
  leftIcon,
  rightIcon,
  testID,
  onPress,
  disabled = false,
  accessibilityLabel,
  accessibilityRole,
  analyticsName,
  style,
}: IconCardProps) => {
  const markdownStyles = useMarkdownStyles({ variant: 'bodyNormal' })

  return (
    <StyledIconCard
      testID={testID}
      onPress={onPress}
      style={style}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityRole}
      analyticsName={analyticsName}
    >
      {leftIcon && <IconContainer position="left" {...leftIcon} />}
      <StyledContentContainer>
        {title && <Typography variant="h4">{title}</Typography>}
        {typeof description === 'string' ? (
          <DialogueMarkdown markdown={description} styles={markdownStyles} containerStyles={{}} />
        ) : (
          description
        )}
      </StyledContentContainer>
      {rightIcon && <IconContainer position="right" {...rightIcon} />}
    </StyledIconCard>
  )
}

export default IconCard
